import { useEffect, useRef, useState } from "react";
import Banner from "./sections/PageBanner";
import PageHeader from "./sections/PageHeader";
import Footer from "./sections/PageFooter";
import ReactGA from "react-ga4";

ReactGA.initialize("G-FMCF2WQ3F6");

/* ── Data ───────────────────────────────────────────────────────────── */
const features = [
  {
    label:       'Case Tracking & Management',
    tag:         'Module A',
    headline:    ['From arrest to verdict,', 'every detail captured'],
    description: 'WITS integrates robust case tracking to ensure transparency and accountability in wildlife crime investigations and prosecutions across jurisdictions.',
    points: [
      'Centralized, searchable case database with timelines',
      'Visual monitoring of delays and missed hearings',
      'Detailed logging of sentencing and plea deals',
      'Cross-border suspect and financial trail linkages',
      'Secure whistleblower tip integration',
      'Forensic evidence and courtroom performance logs',
    ],
    image: '/images/penguin.jpg',
    dark:  true,
  },
  {
    label:       'Analytics & Reporting',
    tag:         'Module B',
    headline:    ['Raw data transformed', 'into actionable intelligence'],
    description: 'WITS converts field records into policy-grade insights that guide enforcement strategies, judicial accountability, and conservation planning.',
    points: [
      'Crime hotspot and seasonal pattern analysis',
      'Modus operandi and syndicate behaviour profiling',
      'Judicial performance and conviction rate tracking',
      'Cross-border criminal network mapping',
      'Live dashboards and stakeholder report generation',
      'Legal gap identification for policy reform',
    ],
    image: '/images/leopard.jpg',
    dark:  false,
  },
  {
    label:       'Geographical Mapping',
    tag:         'Module C',
    headline:    ['Location intelligence', 'across the continent'],
    description: 'Interactive mapping tools provide location-based intelligence to detect crime patterns, visualize trafficking corridors, and optimize field responses.',
    points: [
      'Real-time interactive crime heatmaps',
      'Geo-tagged case tracking from arrest to court',
      'Transnational trafficking route visualisation',
      'Layered data with parks and migration routes',
      'Drone, patrol, and live alert integration',
      'Public-facing advocacy map views',
    ],
    image: '/images/elephant.jpg',
    dark:  true,
  },
  {
    label:       'Incident Reporting',
    tag:         'Module D',
    headline:    ['Field intelligence,', 'documented in seconds'],
    description: 'A centralized reporting tool for real-time documentation and secure submission of intelligence directly from rangers, scouts, and community members.',
    points: [
      'Digital logbook for crimes and suspicious activity',
      'Photo, video, voice note, and document uploads',
      'Auto-tagged GPS locations mapped on WITS',
      'Follow-up feedback for non-anonymous reporters',
      'Prosecution-ready digital evidence trail',
    ],
    image: '/images/v1/img15.jpg',
    dark:  false,
  },
];

/* ── Scroll-reveal hook ─────────────────────────────────────────────── */
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const o = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.12 }
    );
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);
  return { ref, visible };
}

/* ── Check icon ─────────────────────────────────────────────────────── */
function Check({ dark }: { dark: boolean }) {
  return (
    <svg
      className="shrink-0 mt-0.5 w-4 h-4"
      style={{ color: dark ? '#4ade80' : '#16a34a' }}
      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

/* ── Feature section ────────────────────────────────────────────────── */
function FeatureSection({ f, index }: { f: typeof features[0]; index: number }) {
  const { ref, visible } = useReveal();
  const imageRight = index % 2 === 0;

  const bg  = f.dark ? '#052e16' : '#fafaf7';
  const hc  = f.dark ? '#ffffff' : '#111827';
  const sc  = f.dark ? 'rgba(255,255,255,0.58)' : '#6b7280';
  const tc  = f.dark ? '#4ade80' : '#16a34a';
  const pc  = f.dark ? 'rgba(255,255,255,0.75)' : '#374151';
  const dc  = f.dark ? 'rgba(255,255,255,0.07)' : '#e5e7eb';

  const textBlock = (
    <div className={`flex flex-col gap-8 reveal ${visible ? 'visible' : ''}`}>
      <div>
        <span className="flex items-center gap-3 mb-5">
          <span className="h-px w-8" style={{ background: tc }} />
          <span
            className="text-xs tracking-[0.2em] uppercase font-medium"
            style={{ fontFamily: 'var(--font-body)', color: tc }}
          >
            {f.tag}
          </span>
        </span>
        <h2
          className="text-4xl lg:text-5xl font-bold leading-[1.1] mb-5"
          style={{ fontFamily: 'var(--font-display)', color: hc }}
        >
          {f.headline[0]}
          <br />
          <span style={{ color: tc }}>{f.headline[1]}</span>
        </h2>
        <p
          className="text-base leading-relaxed max-w-lg"
          style={{ fontFamily: 'var(--font-body)', color: sc }}
        >
          {f.description}
        </p>
      </div>

      <div
        className="grid sm:grid-cols-2 gap-x-6 gap-y-3 pt-6"
        style={{ borderTop: `1px solid ${dc}` }}
      >
        {f.points.map((point, i) => (
          <div key={i} className="flex items-start gap-2">
            <Check dark={f.dark} />
            <span
              className="text-sm leading-snug"
              style={{ fontFamily: 'var(--font-body)', color: pc }}
            >
              {point}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const imageBlock = (
    <div className={`reveal reveal-delay-2 ${visible ? 'visible' : ''}`}>
      <div
        className="relative rounded-2xl overflow-hidden"
        style={{ boxShadow: f.dark ? '0 32px 64px rgba(0,0,0,0.5)' : '0 32px 64px rgba(5,46,22,0.14)' }}
      >
        <img
          src={f.image}
          alt={f.label}
          className="w-full h-[480px] object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to top, rgba(5,46,22,0.55) 0%, transparent 55%)',
          }}
        />
        <div
          className="absolute top-5 left-5 px-3 py-1.5 rounded-lg"
          style={{
            background:    'rgba(5,46,22,0.85)',
            border:        '1px solid rgba(74,222,128,0.3)',
            fontFamily:    'var(--font-body)',
            fontSize:      10,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color:         '#4ade80',
            fontWeight:    600,
            backdropFilter: 'blur(8px)',
          }}
        >
          {f.label}
        </div>
      </div>
    </div>
  );

  return (
    <section
      ref={ref}
      className="w-full py-24 lg:py-32 overflow-hidden"
      style={{ background: bg }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {imageRight ? textBlock : imageBlock}
          {imageRight ? imageBlock : textBlock}
        </div>
      </div>
    </section>
  );
}

/* ── Page hero ──────────────────────────────────────────────────────── */
function PageHero() {
  return (
    <div
      className="relative w-full overflow-hidden flex flex-col justify-end"
      style={{ height: '52vh', minHeight: 360, background: '#052e16' }}
    >
      <img
        src="/images/v1/img12.jpg"
        alt=""
        className="absolute inset-0 w-full h-full object-cover select-none"
        style={{ opacity: 0.3 }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#052e16]/20 via-transparent to-[#052e16]/95 pointer-events-none" />
      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-10 pb-14">
        <span className="flex items-center gap-3 mb-4">
          <span className="h-px w-8 bg-green-400" />
          <span
            className="text-green-400 text-xs tracking-[0.22em] uppercase font-medium"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            Platform Capabilities
          </span>
        </span>
        <h1
          className="text-5xl lg:text-6xl font-bold text-white leading-tight"
          style={{ fontFamily: 'var(--font-display)', textShadow: '0 2px 24px rgba(0,0,0,0.5)' }}
        >
          What <span className="text-green-400">WITS</span> Does
        </h1>
        <p
          className="mt-4 text-white/65 text-base max-w-xl leading-relaxed"
          style={{ fontFamily: 'var(--font-body)', textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}
        >
          Four integrated modules that turn field intelligence into prosecutable evidence,
          policy reform, and measurable conservation outcomes.
        </p>
      </div>
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────────────── */
function FeaturesPage() {
  ReactGA.send({ hitType: "pageview", page: "/features", title: "Features" });
  return (
    <>
      <Banner />
      <PageHeader />
      <PageHero />
      {features.map((f, i) => (
        <FeatureSection key={f.tag} f={f} index={i} />
      ))}
      <Footer />
    </>
  );
}

export default FeaturesPage;
